import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@crowdfpl/ui-web/tooltip';
import { formatRatingValue } from '@/helpers/duelCard';
import { getFormulaTooltip } from '@/helpers/ratingFormula';
import { cn } from '@/helpers/utils';

interface FormulaParams {
  P: number;
  kBase: number;
  kPop: number;
  kEarly: number;
  ratingMultiplier?: number;
}

interface PotentialRatingBadgesProps {
  win: number;
  lose: number;
  tie: number;
  isPlayer1: boolean;
  formulaParams?: FormulaParams;
  totalVotes: number;
  showDebug: boolean;
}

function RatingBadge({
  value,
  className,
  formula,
  showDebug,
}: {
  value: number;
  className: string;
  formula: string;
  showDebug: boolean;
}) {
  const badgeClasses = cn(
    'px-1 py-0.5 rounded text-[8px] sm:text-[9px] font-semibold whitespace-nowrap',
    className,
    showDebug && 'cursor-help',
  );
  if (showDebug) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={badgeClasses}>{formatRatingValue(value)}</span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs whitespace-pre-wrap font-mono text-[10px] p-2">
            {formula}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  return <span className={badgeClasses}>{formatRatingValue(value)}</span>;
}

export function PotentialRatingBadges({
  win,
  lose,
  tie,
  isPlayer1,
  formulaParams,
  totalVotes,
  showDebug,
}: PotentialRatingBadgesProps) {
  const hasFormulas = formulaParams && totalVotes > 0;
  const mult = formulaParams?.ratingMultiplier ?? 1;

  return (
    <div className={`flex flex-col ${isPlayer1 ? '' : 'items-end'}`}>
      <span className="mb-0.5 text-[7px] leading-normal text-muted-foreground sm:text-[8px]">Potential rating</span>
      <div className={`flex items-center gap-0.5 ${isPlayer1 ? '' : 'flex-row-reverse'}`}>
        {hasFormulas ? (
          <RatingBadge
            value={win}
            className="bg-green-400 text-green-950"
            formula={getFormulaTooltip(
              'win',
              formulaParams.P,
              formulaParams.kBase,
              formulaParams.kPop,
              formulaParams.kEarly,
              win,
              mult,
            )}
            showDebug={showDebug}
          />
        ) : (
          <span className="px-1 py-0.5 rounded text-[8px] sm:text-[9px] font-semibold bg-green-400 text-green-950 whitespace-nowrap">
            {formatRatingValue(win)}
          </span>
        )}
        {hasFormulas ? (
          <RatingBadge
            value={lose}
            className="bg-red-400 text-red-950"
            formula={getFormulaTooltip(
              'lose',
              formulaParams.P,
              formulaParams.kBase,
              formulaParams.kPop,
              formulaParams.kEarly,
              lose,
              mult,
            )}
            showDebug={showDebug}
          />
        ) : (
          <span className="px-1 py-0.5 rounded text-[8px] sm:text-[9px] font-semibold bg-red-400 text-red-950 whitespace-nowrap">
            {formatRatingValue(lose)}
          </span>
        )}
        {hasFormulas ? (
          <RatingBadge
            value={tie}
            className="bg-amber-400 text-amber-950"
            formula={getFormulaTooltip(
              'tie',
              formulaParams.P,
              formulaParams.kBase,
              formulaParams.kPop,
              formulaParams.kEarly,
              tie,
              mult,
            )}
            showDebug={showDebug}
          />
        ) : (
          <span className="px-1 py-0.5 rounded text-[8px] sm:text-[9px] font-semibold bg-amber-400 text-amber-950 whitespace-nowrap">
            {formatRatingValue(tie)}
          </span>
        )}
      </div>
    </div>
  );
}
